import React from 'react';
import { CharacterStats, StatType } from '../../../shared/CharacterTypes';
import type { ProgressionCostOfferWire } from '../../../shared/types/ProgressionCost';
import { useGame } from '../contexts/GameContext';
import { useWithLiveVitals } from '../hooks/usePlayerVital';
import { uiActionBridge } from '../framework/uiActionBridge';
import { PrimaryStatsPanel } from './Character/Stats/PrimaryStatsPanel';
import { CombatStatsPanel } from './Character/Stats/CombatStatsPanel';
import { ResourceStatsPanel } from './Character/Stats/ResourceStatsPanel';
import { UtilityStatsPanel } from './Character/Stats/UtilityStatsPanel';
import { StatsSummaryPanel } from './Character/Stats/StatsSummaryPanel';

export { MiniStatsDisplay } from './Character/Stats/MiniStatsDisplay';

interface CharacterStatsUIProps {
  stats: CharacterStats;
  statPoints: number;
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  characterClass?: string;
  onStatIncrease?: (stat: StatType, amount?: number) => void;
  playerGold?: number;
  statRespecOffer?: ProgressionCostOfferWire | null;
  onRespecStats?: () => void;
  className?: string;
}

export const CharacterStatsUI: React.FC<CharacterStatsUIProps> = ({
  stats,
  statPoints,
  health,
  maxHealth,
  mana,
  maxMana,
  characterClass,
  onStatIncrease,
  playerGold = 0,
  statRespecOffer = null,
  onRespecStats,
  className = '',
}) => (
  <div className={`character-stats-content ${className}`} data-testid="character-stats-body">
    <div
      className="character-stats-ledger"
      data-character-scroll-owner="stats"
      tabIndex={0}
      aria-label="Character stat ledger"
    >
      <StatsSummaryPanel statPoints={statPoints} />
      <div className="character-stats-grid">
        <PrimaryStatsPanel
          stats={stats}
          statPoints={statPoints}
          characterClass={characterClass}
          onStatIncrease={onStatIncrease}
          playerGold={playerGold}
          statRespecOffer={statRespecOffer}
          onRespecStats={onRespecStats}
        />
        <CombatStatsPanel stats={stats} characterClass={characterClass} />
        <ResourceStatsPanel
          health={health}
          maxHealth={maxHealth}
          mana={mana}
          maxMana={maxMana}
        />
        <UtilityStatsPanel stats={stats} characterClass={characterClass} />
      </div>
    </div>
  </div>
);

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export const CharacterStatsContent: React.FC = () => {
  // HP/MP from the vitals store, not the React player (plan 21 §8.3).
  const player = useWithLiveVitals(useGame().player);
  const snapshotReady = player != null
    && player.stats != null
    && isFiniteNumber(player.statPoints)
    && isFiniteNumber(player.health)
    && isFiniteNumber(player.maxHealth)
    && isFiniteNumber(player.mana)
    && isFiniteNumber(player.maxMana);

  if (!snapshotReady) {
    return (
      <div className="character-stats-loading" role="status">
        Waiting for the authoritative stat and resource snapshot.
      </div>
    );
  }

  return (
    <CharacterStatsUI
      stats={player.stats}
      statPoints={player.statPoints}
      health={player.health}
      maxHealth={player.maxHealth}
      mana={player.mana}
      maxMana={player.maxMana}
      characterClass={player.class ?? (player as any).characterClass}
      onStatIncrease={(stat, amount) => uiActionBridge.dispatch('updateStats', amount && amount > 1 ? { stat, amount } : { stat })}
      playerGold={player.inventory?.gold ?? 0}
      statRespecOffer={player.progressionCostOffers?.statRespec ?? null}
      onRespecStats={() => uiActionBridge.dispatch('respecStats', {})}
    />
  );
};
